import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Handle, Position, useReactFlow } from 'reactflow';
import { Card, Button, message } from 'antd';
import { SaveOutlined, PictureOutlined } from '@ant-design/icons';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import apiClient from '../../../api/client';
import { analyzeImage } from '../../../utils/colorUtils';
import NodeLabel from './NodeLabel';
import { useNodeTitle } from './useNodeTitle';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE, NODE_BG } from './nodeStyles';
import CustomResizer from './CustomResizer';

const ColorSpaceViz = ({ samples }) => {
    // Render points in 3D Lab space or RGB cube
    // Mapping normalized LAB/RGB to 3D coords

    // Using simple RGB cube mapping centered at 0
    const points = useMemo(() => {
        const positions = new Float32Array(samples.length * 3);
        const colors = new Float32Array(samples.length * 3);

        samples.forEach((s, i) => {
            // Map RGB 0-255 to -1 to 1 range
            positions[i * 3] = (s.rgb[0] / 255) * 2 - 1;
            positions[i * 3 + 1] = (s.rgb[1] / 255) * 2 - 1;
            positions[i * 3 + 2] = (s.rgb[2] / 255) * 2 - 1;

            colors[i * 3] = s.rgb[0] / 255;
            colors[i * 3 + 1] = s.rgb[1] / 255;
            colors[i * 3 + 2] = s.rgb[2] / 255;
        });

        return { positions, colors };
    }, [samples]);

    return (
        <points>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={samples.length}
                    array={points.positions}
                    itemSize={3}
                />
                <bufferAttribute
                    attach="attributes-color"
                    count={samples.length}
                    array={points.colors}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial size={0.12} vertexColors transparent opacity={0.8} />
        </points>
    );
};

const ColorAnalysisNode = ({ data, id, selected }) => {
    const { getNodes, getEdges } = useReactFlow();
    const [imageUrl, setImageUrl] = useState(null);
    const [analysisData, setAnalysisData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { currentUser } = useAuth();
    const [token, setToken] = useState(null);

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    useEffect(() => {
        if (currentUser) {
            currentUser.getIdToken().then(setToken);
        }
    }, [currentUser]);

    // Watch for connection changes to update image
    useEffect(() => {
        const interval = setInterval(() => {
            const tempEdges = getEdges();
            const tempNodes = getNodes();
            const imageEdge = tempEdges.find(e => e.target === id && e.targetHandle === 'image');

            if (imageEdge) {
                const sourceNode = tempNodes.find(n => n.id === imageEdge.source);
                if (sourceNode?.data?.src && sourceNode.data.src !== imageUrl) {
                    setImageUrl(sourceNode.data.src);
                } else if (!sourceNode) {
                    // Edge exists but source missing?
                }
            } else {
                if (imageUrl) setImageUrl(null);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [getNodes, getEdges, id, imageUrl]);

    // Perform analysis whenever imageUrl changes
    useEffect(() => {
        if (!imageUrl) {
            setAnalysisData(null);
            return;
        }

        let active = true;
        setLoading(true);

        const runAnalysis = async () => {
            try {
                // Use backend proxy to bypass CORS for analysis
                // We construct the proxy URL pointing to our own backend
                const tokenParam = token ? `&token=${token}` : '';
                const proxyUrl = `${apiClient.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(imageUrl)}${tokenParam}`;

                // Create a separate image object for analysis
                const img = new window.Image();
                img.crossOrigin = "Anonymous"; // Still needed for canvas processing even with proxy
                img.src = proxyUrl;

                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                });

                if (!active) return;

                const results = await analyzeImage(img);
                if (active) {
                    setAnalysisData(results);
                    setLoading(false);
                }
            } catch (err) {
                console.error("Color analysis failed:", err);
                if (active) {
                    // Fallback to direct URL if proxy fails (might work if CORS is configured correctly eventually)
                    try {
                        console.log("Proxy failed, retrying direct URL...");
                        const directImg = new window.Image();
                        directImg.crossOrigin = "Anonymous";
                        directImg.src = imageUrl;

                        await new Promise((resolve, reject) => {
                            directImg.onload = resolve;
                            directImg.onerror = reject;
                        });

                        if (!active) return;
                        const results = await analyzeImage(directImg);
                        if (active) {
                            setAnalysisData(results);
                            setLoading(false);
                        }
                    } catch (retryErr) {
                        console.error("Direct analysis also failed:", retryErr);
                        message.warning("Could not analyze image colors (CORS restriction).");
                        setLoading(false);
                    }
                }
            }
        };

        runAnalysis();

        return () => { active = false; };
    }, [imageUrl]);

    const savePalette = async () => {
        if (!analysisData) return;
        try {
            await apiClient.post('/color-palettes', {
                name: `Palette ${new Date().toLocaleString()}`,
                canvasId: data.canvasId,
                colors: analysisData.palette.map(p => ({ color: p.color, weight: p.weight })), // Save with weights
                imageUrl: imageUrl,
                metadata: analysisData.stats
            });
            message.success('Palette saved!');
        } catch (err) {
            message.error('Failed to save palette');
        }
    };

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={400} // Taller default for this node due to stats/3d
                onResizeEnd={(e, params) => data.onResize && data.onResize(id, params)}
            />

            <Card
                title={
                    <NodeLabel
                        label={data.label || 'Color Analysis'}
                        title={data.nodeTitle || 'Color Analysis'}
                        iconType="bg-colors"
                        color="#eb2f96"
                        onTitleChange={onTitleChange}
                    />
                }
                style={{ width: '100%', height: '100%', backgroundColor: NODE_BG, border: '1px solid #30363d', display: 'flex', flexDirection: 'column' }}
                bodyStyle={{ padding: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
                <Handle
                    type="target"
                    position={Position.Left}
                    id="image"
                    style={getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible)}
                />

                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 10, flex: 1, overflow: 'hidden' }} className="nodrag">
                    {/* 1. Image Area - Flex grow to fill space */}
                    {imageUrl ? (
                        <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0, background: '#000', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img
                                    src={imageUrl}
                                    alt="Analysis Target"
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                            </div>

                            {/* Overlay Dots (sampled points) - Only show if analysis succeeded */}
                            {analysisData && (
                                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                                    {analysisData.samples.slice(0, 50).map((s, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                position: 'absolute',
                                                left: `${s.x * 100}%`,
                                                top: `${s.y * 100}%`,
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: s.color,
                                                border: '2px solid rgba(255,255,255,0.7)',
                                                transform: 'translate(-50%, -50%)',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666', border: '1px dashed #444', minHeight: 150 }}>
                            Connect Image
                        </div>
                    )}

                    {/* 2. Palette Strip - Fixed height */}
                    {analysisData && (
                        <div style={{ display: 'flex', height: 30, flexShrink: 0, borderRadius: 4, overflow: 'hidden' }}>
                            {analysisData.palette.map((c, i) => (
                                <div
                                    key={i}
                                    style={{
                                        flex: c.weight, // Weighted width
                                        backgroundColor: c.color,
                                        transition: 'all 0.3s ease'
                                    }}
                                    title={`${c.color} (${Math.round(c.weight * 100)}%)`}
                                />
                            ))}
                        </div>
                    )}

                    {/* 3. Stats & 3D Viz Split */}
                    {analysisData && (
                        <div style={{ display: 'flex', gap: 10, height: 200, flexShrink: 0 }}>
                            {/* Stats Panel */}
                            <div style={{ flex: 1, fontSize: 12, color: '#ccc', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 5 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Avg Light:</span>
                                    <span>{Math.round(analysisData.stats.avgLightness * 100)}%</span>
                                </div>
                                <div style={{ height: 4, background: '#333', borderRadius: 2 }}>
                                    <div style={{ width: `${analysisData.stats.avgLightness * 100}%`, height: '100%', background: '#ccc' }} />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                                    <span>Avg Chroma:</span>
                                    <span>{analysisData.stats.avgChroma.toFixed(2)}</span>
                                </div>
                                <div style={{ height: 4, background: '#333', borderRadius: 2 }}>
                                    <div style={{ width: `${Math.min(analysisData.stats.avgChroma * 100, 100)}%`, height: '100%', background: '#ccc' }} />
                                </div>

                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<SaveOutlined />}
                                    onClick={savePalette}
                                    style={{ marginTop: 'auto' }}
                                >
                                    Save Palette
                                </Button>
                            </div>

                            {/* 3D Viz */}
                            <div style={{ flex: 1, border: '1px solid #444', borderRadius: 4, minWidth: 0, overflow: 'hidden' }}>
                                <Canvas
                                    style={{ width: '100%', height: '100%' }}
                                    camera={{ position: [1.8, 1.8, 1.8], fov: 40 }}
                                >
                                    <ambientLight intensity={0.5} />
                                    <pointLight position={[10, 10, 10]} />
                                    <ColorSpaceViz samples={analysisData.samples} />
                                    <mesh>
                                        <boxGeometry args={[2, 2, 2]} />
                                        <meshBasicMaterial wireframe color="#444" transparent opacity={0.3} />
                                    </mesh>
                                    <OrbitControls autoRotate autoRotateSpeed={1} enableZoom={true} />
                                </Canvas>
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default ColorAnalysisNode;
